import { useState, useMemo } from "react"
import {DollarSign, Percent, Clock, TrendingUp, ChevronDown, BarChart3, PiggyBank} from 'lucide-react'

interface Preset {
  label: string
  amount: number
  rate: number
  termYears: number
}

const presets: Preset[] = [
  { label: "Custom", amount: 250000, rate: 8, termYears: 10 },
  { label: "Subway Franchise Financing", amount: 200000, rate: 7, termYears: 10 },
  { label: "McDonald's Franchise Loan", amount: 1000000, rate: 6, termYears: 20 },
  { label: "SBA 7(a) Loan for Restaurants", amount: 500000, rate: 8, termYears: 10 },
  { label: "Restaurant Equipment Financing", amount: 50000, rate: 10, termYears: 5 },
  { label: "New Restaurant Startup", amount: 250000, rate: 12, termYears: 7 },
]

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatCurrencyFull(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export default function LoanCalculator() {
  const [selectedPreset, setSelectedPreset] = useState(0)
  const [loanAmount, setLoanAmount] = useState(250000)
  const [interestRate, setInterestRate] = useState(8)
  const [termYears, setTermYears] = useState(10)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handlePresetChange = (index: number) => {
    setSelectedPreset(index)
    const preset = presets[index]
    setLoanAmount(preset.amount)
    setInterestRate(preset.rate)
    setTermYears(preset.termYears)
    setIsDropdownOpen(false)
  }

  const { monthlyPayment, totalInterest, totalCost } = useMemo(() => {
    const principal = loanAmount
    const monthlyRate = interestRate / 100 / 12
    const numPayments = termYears * 12

    if (monthlyRate === 0) {
      const mp = principal / numPayments
      return {
        monthlyPayment: mp,
        totalInterest: 0,
        totalCost: principal,
      }
    }

    const mp = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)
    const tc = mp * numPayments
    const ti = tc - principal

    return {
      monthlyPayment: mp,
      totalInterest: ti,
      totalCost: tc,
    }
  }, [loanAmount, interestRate, termYears])

  const principalPercent = totalCost > 0 ? (loanAmount / totalCost) * 100 : 50

  return (
    <div id="calculator" className="scroll-mt-24">
      <div className="bg-[#0F2240]/80 backdrop-blur-sm rounded-2xl border border-white/5 overflow-hidden shadow-2xl shadow-black/20">
        {/* Header */}
        <div className="px-6 sm:px-8 py-6 border-b border-white/5 bg-gradient-to-r from-emerald-500/5 to-transparent">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Restaurant Loan Calculator</h2>
          <p className="text-slate-400 text-sm mt-1">Adjust the sliders or select a preset to estimate your payments</p>
        </div>

        <div className="p-6 sm:p-8">
          {/* Preset Dropdown */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-300 mb-2">What are you financing?</label>
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3.5 bg-[#0A1628] border border-white/10 rounded-xl text-white text-sm hover:border-emerald-500/30 transition-colors"
              >
                <span>{presets[selectedPreset].label}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {isDropdownOpen ? (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#0F2240] border border-white/10 rounded-xl overflow-hidden shadow-xl z-20">
                  {presets.map((preset, index) => (
                    <button
                      key={preset.label}
                      onClick={() => handlePresetChange(index)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        index === selectedPreset
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "text-slate-300 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <span className="font-medium">{preset.label}</span>
                      {index > 0 ? (
                        <span className="block text-xs text-slate-500 mt-0.5">
                          {formatCurrency(preset.amount)} at {preset.rate}% for {preset.termYears} years
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Sliders */}
          <div className="space-y-8">
            {/* Loan Amount */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  Loan Amount
                </label>
                <span className="text-lg font-bold text-white">{formatCurrency(loanAmount)}</span>
              </div>
              <input
                type="range"
                min={10000}
                max={2000000}
                step={5000}
                value={loanAmount}
                onChange={(e) => { setLoanAmount(Number(e.target.value)); setSelectedPreset(0) }}
                className="w-full"
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-500">$10,000</span>
                <span className="text-xs text-slate-500">$2,000,000</span>
              </div>
            </div>

            {/* Interest Rate */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                  <Percent className="w-4 h-4 text-emerald-400" />
                  Interest Rate (APR)
                </label>
                <span className="text-lg font-bold text-white">{interestRate.toFixed(1)}%</span>
              </div>
              <input
                type="range"
                min={1}
                max={25}
                step={0.1}
                value={interestRate}
                onChange={(e) => { setInterestRate(Number(e.target.value)); setSelectedPreset(0) }}
                className="w-full"
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-500">1%</span>
                <span className="text-xs text-slate-500">25%</span>
              </div>
            </div>

            {/* Loan Term */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                  <Clock className="w-4 h-4 text-emerald-400" />
                  Loan Term
                </label>
                <span className="text-lg font-bold text-white">{termYears} {termYears === 1 ? "Year" : "Years"} ({termYears * 12} months)</span>
              </div>
              <input
                type="range"
                min={1}
                max={30}
                step={1}
                value={termYears}
                onChange={(e) => { setTermYears(Number(e.target.value)); setSelectedPreset(0) }}
                className="w-full"
              />
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-500">1 Year</span>
                <span className="text-xs text-slate-500">30 Years</span>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-xl p-5 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-medium text-emerald-400 uppercase tracking-wider">Monthly Payment</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white">{formatCurrencyFull(monthlyPayment)}</p>
            </div>
            <div className="bg-[#0A1628]/60 rounded-xl p-5 border border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Interest</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white">{formatCurrency(totalInterest)}</p>
            </div>
            <div className="bg-[#0A1628]/60 rounded-xl p-5 border border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <PiggyBank className="w-4 h-4 text-sky-400" />
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Cost</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white">{formatCurrency(totalCost)}</p>
            </div>
          </div>

          {/* Visual Breakdown Bar */}
          <div className="mt-6 bg-[#0A1628]/60 rounded-xl p-5 border border-white/5">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Payment Breakdown</p>
            <div className="h-4 rounded-full overflow-hidden flex bg-slate-800">
              <div
                className="bg-emerald-500 transition-all duration-500 ease-out rounded-l-full"
                style={{ width: `${principalPercent}%` }}
              />
              <div
                className="bg-amber-500 transition-all duration-500 ease-out rounded-r-full"
                style={{ width: `${100 - principalPercent}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2.5">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-xs text-slate-400">Principal: {formatCurrency(loanAmount)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                <span className="text-xs text-slate-400">Interest: {formatCurrency(totalInterest)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
